import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Calculator, Euro, HelpCircle, CheckCircle, AlertTriangle, 
  ChevronRight, ArrowRight, Sparkles 
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section de simulation personnalisée des aides
 * Utilise les données du backend pour garantir la cohérence des calculs
 */
export default function AidSimulatorSection({ 
  workTypes = [], 
  incomeCategory,
  housingSurface,
  // Nouveaux props: données du backend
  backendResults = null,
  leadId = null,
  department = null,
  onSimulationComplete 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    // Surfaces d'isolation
    surface_combles: "",
    surface_murs: "",
    surface_planchers: "",
    // Équipements
    nombre_fenetres: "",
    // Devis
    devis_total_ht: "",
    // DPE
    dpe_avant: "",
    dpe_apres: "",
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Utiliser les données du backend si disponibles
  const getBackendAmounts = () => {
    if (!backendResults) return null;
    
    // Extraire les montants du backend
    const byPayer = backendResults.by_payer || {};
    const totalMPR = byPayer['ANAH'] || 0;
    const totalCEE = byPayer['Fournisseurs d\'énergie (CEE)'] || 0;
    const tvaEconomie = byPayer['Fiscalité (TVA)'] || 0;
    
    return { totalMPR, totalCEE, tvaEconomie };
  };

  const calculateAids = async () => {
    setLoading(true);
    
    const surfaceCombles = parseInt(formData.surface_combles) || 0;
    const surfaceMurs = parseInt(formData.surface_murs) || 0;
    const surfacePlanchers = parseInt(formData.surface_planchers) || 0;
    const nombreFenetres = parseInt(formData.nombre_fenetres) || 0;
    const devisTotal = parseInt(formData.devis_total_ht) || 0;

    // Utiliser les montants du backend si disponibles
    const backendAmounts = getBackendAmounts();
    
    let totalMPR = 0;
    let totalCEE = 0;
    let tvaEconomie = 0;
    const details = [];

    if (backendAmounts && devisTotal === 0) {
      // Utiliser directement les montants du backend si pas de devis personnalisé
      totalMPR = backendAmounts.totalMPR;
      totalCEE = backendAmounts.totalCEE;
      tvaEconomie = backendAmounts.tvaEconomie;
    } else {
      // Recalculer avec les données saisies via l'API
      try {
        const response = await axios.post(`${API}/simulate-aids`, {
          work_types: workTypes,
          income_category: incomeCategory,
          housing_surface: housingSurface,
          department: department || "75",
          devis_total_ht: devisTotal || null,
          surfaces: {
            combles: surfaceCombles,
            murs: surfaceMurs,
            planchers: surfacePlanchers
          },
          nombre_fenetres: nombreFenetres
        });
        
        const data = response.data;
        totalMPR = data.total_mpr || 0;
        totalCEE = data.total_cee || 0;
        tvaEconomie = data.tva_economie || 0;
        
        if (data.details) {
          data.details.forEach(d => {
            details.push({ 
              label: d.label, 
              mpr: d.mpr || 0, 
              cee: d.cee || 0 
            });
          });
        }
      } catch (error) {
        console.error("Erreur lors de la simulation:", error);
        // Fallback sur les montants du backend
        if (backendAmounts) {
          totalMPR = backendAmounts.totalMPR;
          totalCEE = backendAmounts.totalCEE;
        }
        // TVA calculée localement si devis fourni
        if (devisTotal > 0) {
          tvaEconomie = Math.round(devisTotal * 0.145);
        }
      }
    }

    // Si on a un devis et pas de TVA calculée, la calculer
    if (devisTotal > 0 && tvaEconomie === 0) {
      tvaEconomie = Math.round(devisTotal * 0.145);
    }

    // Plafonds MaPrimeRénov'
    const plafondMPR = { bleu: 11000, jaune: 9000, violet: 6000, rose: 0 }[incomeCategory] || 0;
    totalMPR = Math.min(totalMPR, plafondMPR);

    const totalAides = totalMPR + totalCEE + tvaEconomie;
    const estimatedCost = devisTotal || (backendResults?.total_estimated_cost || (totalMPR + totalCEE + 10000));
    const resteACharge = Math.max(0, estimatedCost - (totalMPR + totalCEE));

    setResults({
      totalMPR,
      totalCEE,
      tvaEconomie,
      totalAides,
      resteACharge,
      devisTotal: estimatedCost,
      details,
      plafondMPR
    });

    if (onSimulationComplete) {
      onSimulationComplete({ totalMPR, totalCEE, tvaEconomie, totalAides, resteACharge });
    }

    setLoading(false);
  };

  // Calculer automatiquement avec les données du backend à l'ouverture
  const handleOpen = () => {
    setIsOpen(true);
    // Si on a des résultats du backend, les afficher directement
    if (backendResults && !results) {
      const backendAmounts = getBackendAmounts();
      if (backendAmounts) {
        const estimatedCost = backendResults.total_estimated_cost || 15000;
        const tva = backendResults.by_payer?.['Fiscalité (TVA)'] || 0;
        
        // Extraire les détails par travaux depuis eligible_aids
        const details = backendResults.requested_works?.map(w => {
          let mpr = 0;
          let cee = 0;
          
          // Parser les montants depuis eligible_aids
          if (w.eligible_aids) {
            w.eligible_aids.forEach(aid => {
              // Extraire le montant numérique du display (ex: "5 000€" -> 5000)
              const amountMatch = aid.display?.match(/[\d\s]+/);
              if (amountMatch) {
                const amount = parseInt(amountMatch[0].replace(/\s/g, '')) || 0;
                if (aid.code === 'MPR') {
                  mpr = amount;
                } else if (aid.code === 'CEE') {
                  cee = amount;
                }
              }
            });
          }
          
          return {
            label: w.label,
            mpr: mpr,
            cee: cee
          };
        }) || [];
        
        setResults({
          totalMPR: backendAmounts.totalMPR,
          totalCEE: backendAmounts.totalCEE,
          tvaEconomie: tva,
          totalAides: backendAmounts.totalMPR + backendAmounts.totalCEE + tva,
          resteACharge: Math.max(0, estimatedCost - backendAmounts.totalMPR - backendAmounts.totalCEE),
          devisTotal: estimatedCost,
          details: details,
          plafondMPR: { bleu: 11000, jaune: 9000, violet: 6000, rose: 0 }[incomeCategory] || 0
        });
      }
    }
  };

  const hasIsolation = workTypes.some(w => w.includes("isolation"));
  const hasFenetres = workTypes.some(w => w.includes("fenetres"));

  return (
    <>
      {/* Bouton d'ouverture */}
      <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2d6b47] rounded-xl p-5 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <Calculator className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">Calculez précisément vos aides</h3>
            <p className="text-white/80 text-sm mb-3">
              Répondez à quelques questions pour obtenir une estimation personnalisée basée sur vos travaux réels.
            </p>
            <Button 
              onClick={handleOpen}
              className="bg-white text-[#1A4D2E] hover:bg-white/90"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Lancer la simulation
            </Button>
          </div>
        </div>
      </div>

      {/* Modal de simulation */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
              <Calculator className="h-5 w-5" />
              Simulation personnalisée
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Formulaire ou Résultats */}
            {!results && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="text-slate-600">
                    <strong>Catégorie de revenus :</strong>{" "}
                    <span className="capitalize font-medium text-[#1A4D2E]">{incomeCategory}</span>
                  </p>
                </div>

                {/* Surfaces d'isolation */}
                {hasIsolation && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-sm text-slate-700 border-b pb-2">
                      📐 Surfaces à isoler (optionnel)
                    </h4>
                    
                    {workTypes.some(w => w.includes("isolation_combles")) && (
                      <div className="space-y-2">
                        <Label htmlFor="surface_combles">Surface des combles (m²)</Label>
                        <Input
                          id="surface_combles"
                          name="surface_combles"
                          type="number"
                          value={formData.surface_combles}
                          onChange={handleChange}
                          placeholder={`Ex: ${housingSurface || 80}`}
                          className="h-11"
                        />
                      </div>
                    )}

                    {workTypes.some(w => w.includes("isolation_murs")) && (
                      <div className="space-y-2">
                        <Label htmlFor="surface_murs">Surface des murs (m²)</Label>
                        <Input
                          id="surface_murs"
                          name="surface_murs"
                          type="number"
                          value={formData.surface_murs}
                          onChange={handleChange}
                          placeholder="Ex: 120"
                          className="h-11"
                        />
                      </div>
                    )}

                    {workTypes.some(w => w.includes("isolation_planchers")) && (
                      <div className="space-y-2">
                        <Label htmlFor="surface_planchers">Surface des planchers (m²)</Label>
                        <Input
                          id="surface_planchers"
                          name="surface_planchers"
                          type="number"
                          value={formData.surface_planchers}
                          onChange={handleChange}
                          placeholder={`Ex: ${housingSurface || 80}`}
                          className="h-11"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Fenêtres */}
                {hasFenetres && (
                  <div className="space-y-2">
                    <Label htmlFor="nombre_fenetres">Nombre de fenêtres à remplacer</Label>
                    <Input
                      id="nombre_fenetres"
                      name="nombre_fenetres"
                      type="number"
                      value={formData.nombre_fenetres}
                      onChange={handleChange}
                      placeholder="Ex: 8"
                      className="h-11"
                    />
                  </div>
                )}

                {/* Devis total */}
                <div className="space-y-2">
                  <Label htmlFor="devis_total_ht">Montant total du devis HT (€) - optionnel</Label>
                  <Input
                    id="devis_total_ht"
                    name="devis_total_ht"
                    type="number"
                    value={formData.devis_total_ht}
                    onChange={handleChange}
                    placeholder="Ex: 25000"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">
                    Laissez vide pour utiliser l'estimation du simulateur.
                  </p>
                </div>

                <Button 
                  onClick={calculateAids} 
                  className="w-full h-12 bg-[#1A4D2E] hover:bg-[#143d24]"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Calcul en cours...
                    </span>
                  ) : (
                    <>
                      <Calculator className="h-4 w-4 mr-2" />
                      Calculer mes aides
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Résultats */}
            {results && (
              <div className="space-y-4">
                {/* Total */}
                <div className="bg-gradient-to-br from-[#E8F5E9] to-[#f0f9f1] rounded-xl p-5 border border-[#1A4D2E]/20">
                  <div className="text-center mb-4">
                    <p className="text-sm text-slate-600 mb-1">Total des aides estimées</p>
                    <p className="text-4xl font-bold text-[#1A4D2E]">
                      {results.totalAides.toLocaleString('fr-FR')} €
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      sur {results.devisTotal.toLocaleString('fr-FR')} € de travaux
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Reste à charge</p>
                      <p className="text-xl font-bold text-[#E07A5F]">
                        {results.resteACharge.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Taux de financement</p>
                      <p className="text-xl font-bold text-[#1A4D2E]">
                        {Math.round(((results.totalMPR + results.totalCEE) / results.devisTotal) * 100)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Détail par aide */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm">Détail des aides</h4>
                  
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">MaPrimeRénov'</span>
                      <span className="font-bold text-[#1A4D2E]">{results.totalMPR.toLocaleString('fr-FR')} €</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Plafond {incomeCategory} : {results.plafondMPR.toLocaleString('fr-FR')} €</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Prime CEE</span>
                      <span className="font-bold text-[#1A4D2E]">{results.totalCEE.toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">TVA réduite (5,5%)</span>
                      <span className="font-bold text-[#1A4D2E]">{results.tvaEconomie.toLocaleString('fr-FR')} €</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Économie par rapport à la TVA 20%</p>
                  </div>
                </div>

                {/* Détail par travaux */}
                {results.details && results.details.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-sm mb-2">Par type de travaux</h4>
                    <div className="space-y-2 text-sm">
                      {results.details.map((d, i) => (
                        <div key={i} className="flex justify-between text-slate-600">
                          <span>{d.label}</span>
                          <span className="font-medium">{((d.mpr || 0) + (d.cee || 0)).toLocaleString('fr-FR')} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Cette estimation est indicative. Le montant définitif sera confirmé lors de l'instruction de votre dossier par l'ANAH et votre obligé CEE.
                    </span>
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setResults(null)}
                    className="flex-1"
                  >
                    Modifier
                  </Button>
                  <Button 
                    onClick={() => setIsOpen(false)}
                    className="flex-1 bg-[#1A4D2E] hover:bg-[#143d24]"
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
